import axios, {AxiosError, AxiosRequestConfig} from "axios"
import {Order, SmsSubscriptionResponse} from "../../dtos/orderResponse"
import {Event, EventResponse} from "../../dtos/eventResponse"
import {getAxiosConfigWithAuth, getAccessToken} from "../../utils/auth"
import {
	MultiCreditApiResponse,
	MultipleRefundDetails,
	RefundApiResponse,
	RefundDetails,
} from "./RefundDetails"
import {PaymentHistory} from "./paymentHistory"
import {ShippingAddressChangeRequest, BillingAddressChangeRequest} from "./AddressDetails"
import {
	CreditOrderDetails,
	CreditItemDetails,
	OrderCredits,
	IssuableAmountsForMC,
	MultiLineItemCreditPayload,
} from "./CreditDetails"
import {EmailRequest, SendEmailResponse, SendEmailServiceResponse} from "./SendEmailDetails"
import {LocalStorageHandler} from "../../utils/localStorageHandler"
import {
	ReplaceOrderResponse,
	CommerceOrder,
	OrderReplacementRequest,
	ReplaceOrderResponseV2,
} from "./Order"
import {OrderItemFailure} from "../../dtos/orderFailuresResponse"
import {ReplacementShippingMethodsResponse} from "../../dtos/ReplacementShippingMethodsResponse"
import type {OrderFulfillmentHistory} from "@vp/fulfillment-history"

export default class OrderService {
	public async getPagedOrders(
		customerId: string,
		pageNumber: number,
		pageSize: number
	): Promise<Array<Order>> {
		const res = await axios
			.get(
				`${process.env.REACT_APP_CARE_ORDER_SERVICE}/v1/orders?shopperId=${customerId}&buildEditUrl=true&pageNumber=${pageNumber}&pageSize=${pageSize}&fetchCancellabilityInfo=true`,
				getAxiosConfigWithAuth()
			)
			.then((res) => {
				return res.data as Array<Order>
			})
			.catch((e) => {
				console.error(e)
				return []
			})

		return res
	}

	public async getAllOrders(
		customerId: string,
		initialPage: number,
		initialOrders: Array<Order>
	): Promise<Array<Order>> {
		const pageSize = 100
		let orders = await this.getPagedOrders(customerId, initialPage, pageSize)
		if (orders.length === pageSize) {
			return this.getAllOrders(customerId, initialPage + 1, [...initialOrders, ...orders])
		} else {
			//orders.length < pageSize because it's impossible to have more than the pageSize
			return [...initialOrders, ...orders]
		}
	}

	public async getOrder(orderNumber: string): Promise<Order | null> {
		const res = await axios
			.get(
				`${process.env.REACT_APP_CARE_ORDER_SERVICE}/v1/orders/${orderNumber}`,
				getAxiosConfigWithAuth()
			)
			.then((res) => {
				return res.data as Order
			})
			.catch((e) => {
				return null
			})
		return res
	}

	/**
	 * Fetches the BOP fulfillment history for an order. Care Console fetches this
	 * itself (using Cimpress auth) so it can pass the data directly to the
	 * @vp/fulfillment-history banner, which otherwise relies on @vp/auth (VP
	 * shopper auth) that Care Console does not have.
	 */
	public async getOrderFulfillmentHistory(
		shopperId: string,
		orderNumber: string
	): Promise<OrderFulfillmentHistory | null> {
		const res = await axios
			.get(
				`${process.env.PRODUCTION_BOP_BASE_URL}/ui/shoppers/${shopperId}/orders/${orderNumber}/v2/fulfillmentHistory`,
				getAxiosConfigWithAuth()
			)
			.then((res) => {
				return res.data as OrderFulfillmentHistory
			})
			.catch((e) => {
				console.error("Failed to fetch fulfillment history", e)
				return null
			})
		return res
	}

	public async getCommerceOrder(searchText: string, creationDate: Date): Promise<CommerceOrder> {
		const res = await axios
			.get(
				`${
					process.env.REACT_APP_CARE_ORDER_SERVICE
				}/v1/commerceOrders?searchText=${searchText}&creationDate=${encodeURIComponent(
					creationDate.toString()
				)}`,
				getAxiosConfigWithAuth()
			)
			.then((res) => {
				return res.data as any
			})
			.catch((e) => {
				console.log("Failed to fetch the commerce order " + e)
				return null
			})
		return res
	}

	/**
	 * Returns the event history of an Order
	 * @param   {string} orderNumber    The order number
	 * @param   {number} number         The number of the events that should be returned. To return all the list of events, this parameter should be negative (e.g : -1)
	 * @param   {string} type           Event type
	 * @return  {Event[]}               List of order event
	 */
	public async getOrderEvents(orderNumber: string, limit: number, type: string): Promise<Event[]> {
		const limitParam = limit > 0 ? `limit=${limit}&` : "limit=100&"
		const typeParam = type ? `type=${type}` : ""
		const res = await axios
			.get(
				`${process.env.REACT_APP_CARE_ORDER_SERVICE}/v1/orders/${orderNumber}/events?${limitParam}${typeParam}`,
				getAxiosConfigWithAuth()
			)
			.then((res) => {
				return res.data as Event[]
			})
		return res
	}

	public async getSmsSubscription(orderNumber: string): Promise<SmsSubscriptionResponse> {
		const res = await axios
			.get(
				`${process.env.REACT_APP_CARE_ORDER_SERVICE}/v1/orders/${orderNumber}/smsSubscriptions`,
				getAxiosConfigWithAuth()
			)
			.then((res) => {
				return res.data
			})
			.catch((err) => {
				console.log(`error fetching the sms subscription for the order: ${orderNumber}`)
				return undefined
			})
		return res
	}

	public async getRejectedtEvent(
		orderNumber: string,
		itemId: string,
		limit: number
	): Promise<Event> {
		const res = await this.getOrderEvents(orderNumber, limit, "LINE_ITEM_FULFILLMENT_REJECTED")
			.then((itemEvents: Event[]) => {
				let rejectedItemEvent = itemEvents
					.filter((event) => event.itemInfo[0].itemId === itemId)
					.filter(
						(event) =>
							JSON.parse(event.eventProperties.mcpEvent[0]).eventType === "fulfillerRejected"
					)
				return rejectedItemEvent[0]
			})
			.catch((err) => {
				const event: any = {}
				return event
			})
		return res
	}

	public async getFailedEvent(orderNumber: string, itemId: string): Promise<Event> {
		let res = await this.getOrderEvents(
			orderNumber,
			0,
			"LINE_ITEM_FULFILLMENT_SEND_FAILED_RETRYABLE"
		)
			.then(async (events: Event[]) => {
				if (events && events.length > 0) {
					const itemEvent = events.filter(
						(event) =>
							event.itemInfo[0].itemId === itemId &&
							event.eventProperties &&
							event.eventProperties.mcpApiErrorResponse &&
							event.eventProperties.mcpApiErrorResponse.merchantItemIds &&
							event.eventProperties.mcpApiErrorResponse.merchantItemIds[0] === itemId
					)
					if (itemEvent.length > 0) return itemEvent[0]
				} else {
					return await this.getOrderEvents(orderNumber, 0, "LINE_ITEM_FULFILLMENT_SEND_FAILED")
						.then(async (eventsArray: Event[]) => {
							if (eventsArray && eventsArray.length > 0) {
								const itemEvent = eventsArray.filter((event) => event.itemInfo[0].itemId === itemId)
								if (itemEvent.length > 0) return itemEvent[0]
							}
						})
						.catch((err) => {
							const event: any = {}
							return event
						})
				}
			})
			.catch((err) => {
				const event: any = {}
				return event
			})
		return res
	}

	async cancelLineItem(data: {
		orderNumber: string
		lineItemId: string
		cancelReason: string
		caseId?: string
		isConcession?: boolean
		handleTime?: number
		isMultiSelect?: boolean
	}): Promise<boolean> {
		const {orderNumber, lineItemId, cancelReason, caseId, isConcession, handleTime, isMultiSelect} =
			data
		let url = `${process.env.REACT_APP_CARE_ORDER_SERVICE}/v1/orders/${orderNumber}/lineItems/${lineItemId}?caseId=${caseId}&isConcession=${isConcession}`
		if (handleTime) {
			url = `${url}&handleTime=${handleTime}`
		}
		if (isMultiSelect) {
			url = `${url}&isMultiSelect=${isMultiSelect}`
		}
		const res = await axios
			.patch(
				url,
				{
					state: "cancel requested",
					cancelReason: cancelReason,
				},
				getAxiosConfigWithAuth()
			)
			.then((res) => {
				return true
			})
			.catch((err) => {
				console.error(`error while attempting to cancel an order: ${orderNumber}; ` + err)
				return false
			})

		return res
	}

	async updateDocumentLineItem(data: {
		orderNumber: string
		lineItemId: string
		caseId: string
		handleTime?: number
	}): Promise<any> {
		const {orderNumber, lineItemId, caseId, handleTime} = data
		let url = `${process.env.REACT_APP_CARE_ORDER_SERVICE}/v1/orders/${orderNumber}/lineItems/${lineItemId}/document?caseId=${caseId}`
		if (handleTime) {
			url = `${url}&handleTime=${handleTime}`
		}
		const res = await axios
			.patch(url, {}, getAxiosConfigWithAuth())
			.then((res) => {
				return res
			})
			.catch((err) => {
				console.error(
					`error while attempting to update a document of an order item: orderNumber = ${orderNumber} lineItemId = ${lineItemId}` +
						err
				)
				return false
			})
		return res
	}
	async updateDocumentLineItemV2(data: {
		orderNumber: string
		lineItemIds: string[]
		caseId: string
		handleTime?: number
	}): Promise<any> {
		const {orderNumber, lineItemIds, caseId, handleTime} = data

		let url = `${process.env.REACT_APP_CARE_ORDER_SERVICE}/v1/orders/${orderNumber}/lineItems/document?caseId=${caseId}`

		if (handleTime) {
			url = `${url}&handleTime=${handleTime}`
		}

		const requestBody = {
			lineItemIds: lineItemIds,
		}

		const res = await axios
			.patch(url, requestBody, getAxiosConfigWithAuth())
			.then((res) => {
				return res
			})
			.catch((err) => {
				console.error(
					`error while attempting to update documents for order items: orderNumber = ${orderNumber}, lineItemIds = ${lineItemIds.join(
						", "
					)}` + err
				)
				return false
			})

		return res
	}
	async cancelOrder(
		orderNumber: string,
		cancelReason: string,
		handleTime?: number,
		caseId?: string,
		isConcession?: boolean
	): Promise<boolean> {
		let endpoint = `${process.env.REACT_APP_CARE_ORDER_SERVICE}/v1/orders/${orderNumber}?caseId=${caseId}&isConcession=${isConcession}`
		if (handleTime) {
			endpoint = `${endpoint}&handleTime=${handleTime}`
		}
		const res = await axios
			.patch(
				endpoint,
				{
					orderStatus: "cancel requested",
					cancelReason: cancelReason,
				},
				getAxiosConfigWithAuth()
			)
			.then((res) => {
				return true
			})
			.catch((err) => {
				console.error(`error while attempting to cancel an order: ${orderNumber}; ` + err)
				return false
			})

		return res
	}
	async cancelUncancelledItems(
		order: Order,
		cancelReason: string,
		handleTime?: number,
		caseId?: string,
		isConcession?: boolean
	): Promise<boolean> {
		let allItemsCancelled = false
		const cancellableOrderItems = order.orderData.lineItems.filter(
			(item) => item.isCancellable === true
		)
		if (cancellableOrderItems.length === order.orderData.lineItems.length) {
			allItemsCancelled = await this.cancelOrder(
				order.orderNumber,
				cancelReason,
				handleTime,
				caseId,
				isConcession
			)
		} else {
			const promises = cancellableOrderItems.map(async (item) => {
				return await this.cancelLineItem({
					orderNumber: order.orderNumber,
					lineItemId: item.id,
					cancelReason: cancelReason,
					caseId: caseId,
					isConcession: isConcession,
					handleTime: handleTime,
				})
			})
			await Promise.all(promises)
				.then((results) => {
					if (results.length > 0) {
						allItemsCancelled = results.includes(false) ? false : true
					}
				})
				.catch((error) => {
					console.error("Failed to cancel the remaining uncancelled items")
				})
		}
		return allItemsCancelled
	}

	async replaceLineItem(
		orderId: string,
		lineItemId: string,
		replaceReason: string,
		quantity: number,
		alerted: boolean,
		caseId?: string,
		isConcession?: boolean,
		handleTime?: number,
		isMultiSelect?: boolean
	): Promise<any> {
		let endpoint = `${
			process.env.REACT_APP_CARE_ORDER_SERVICE
		}/v1/orders/${orderId}/lineItems/${lineItemId}/replacements?caseId=${caseId}&alerted=${
			alerted === null || !alerted ? false : alerted
		}&isConcession=${isConcession}`
		if (handleTime) {
			endpoint = `${endpoint}&handleTime=${handleTime}`
		}
		if (isMultiSelect) {
			endpoint = `${endpoint}&isMultiSelect=${isMultiSelect}`
		}
		const res = await axios
			.post(
				endpoint,
				{
					replacementReason: replaceReason,
					quantity: quantity || 1,
				},
				getAxiosConfigWithAuth()
			)
			.then((res) => {
				return res.status === 200
			})
			.catch((err) => {
				console.log(`error while attempting to replace an item: ${lineItemId}; ` + err)
				return ""
			})

		return res
	}

	async replaceOrder(
		orderId: string,
		replaceReason: string,
		alerted: boolean,
		handleTime?: number,
		caseId?: string,
		isConcession?: boolean
	): Promise<ReplaceOrderResponse> {
		let endpoint = `${
			process.env.REACT_APP_CARE_ORDER_SERVICE
		}/v1/orders/${orderId}/replacements?caseId=${caseId}&alerted=${
			alerted === null || !alerted ? false : alerted
		}&isConcession=${isConcession}`
		if (handleTime) {
			endpoint = `${endpoint}&handleTime=${handleTime}`
		}
		const res = await axios
			.post(
				endpoint,
				{
					replacementReason: replaceReason,
				},
				getAxiosConfigWithAuth()
			)
			.then((res) => {
				return {
					code: res.status,
				}
			})
			.catch((err) => {
				console.log(`error while attempting to replace an order: ${orderId}; ` + err)
				return {
					code: err.response.status,
				}
			})

		return res
	}

	async getVatInvoice(params: {
		customerId: string
		orderNumber: string
		emailAddress: string
		isAnonymous: boolean
		caseId?: string
	}): Promise<any> {
		const {customerId, orderNumber, emailAddress, isAnonymous, caseId} = params
		try {
			let vatEndpoint = `${process.env.REACT_APP_CARE_ORDER_SERVICE}/v1/orders/${orderNumber}/vatInvoice?isAnonymous=${isAnonymous}&email=${emailAddress}&shopperId=${customerId}&caseId=${caseId}`
			let config = {
				headers: {
					Authorization: `Bearer ${getAccessToken()}`,
				},
				responseType: "arraybuffer",
				responseEncoding: "binary",
			}
			let data = await axios
				.get(vatEndpoint, config as AxiosRequestConfig)
				.then((response) => {
					return response.data
				})
				.catch((err) => {
					throw err
				})

			const element = document.createElement("a")
			const file = new Blob([data], {type: "application/octet-stream"})
			element.href = URL.createObjectURL(file)
			element.download = `${orderNumber}_vat.pdf`
			document.body.appendChild(element)
			element.click()
			return true
		} catch (err) {
			let error = err as AxiosError
			console.error(
				`Error when generating VAT invoice for shopperId ${customerId} and order ${orderNumber} ${error.code}:${error.message}`
			)
			return false
		}
	}

	async refundOrder(refundDetails: RefundDetails): Promise<boolean> {
		let endpointV1 = `${process.env.REACT_APP_CARE_ORDER_SERVICE}/v1/orders/${
			refundDetails.orderId
		}/refunds?caseId=${refundDetails.caseId}&alerted=${
			refundDetails.alerted === null || !refundDetails.alerted ? false : refundDetails.alerted
		}&isConcession=${refundDetails.isConcession}`
		let endpointV2 = `${process.env.REACT_APP_CARE_ORDER_SERVICE}/v2/orders/${
			refundDetails.orderId
		}/refunds?caseId=${refundDetails.caseId}&shippingOnly=${refundDetails.shippingOnly}&alerted=${
			refundDetails.alerted === null || !refundDetails.alerted ? false : refundDetails.alerted
		}&isConcession=${refundDetails.isConcession}`
		let endpoint = refundDetails.shippingOnly ? endpointV2 : endpointV1
		if (refundDetails.handleTime) {
			endpoint = `${endpoint}&handleTime=${refundDetails.handleTime}`
		}

		if (refundDetails.isOverpaymentAccepted) {
			endpoint = `${endpoint}&isOverpaymentAccepted=${refundDetails.isOverpaymentAccepted}`
		}

		const res = await axios
			.post(
				endpoint,
				{
					refundReason: refundDetails.reason,
				},
				getAxiosConfigWithAuth()
			)
			.then(() => {
				return true
			})
			.catch((err) => {
				console.error("error while attempting to refund an order: " + err)
				return false
			})

		return res
	}

	async refundItem(refundDetails: RefundDetails): Promise<boolean> {
		let endpoint = `${process.env.REACT_APP_CARE_ORDER_SERVICE}/v2/orders/${
			refundDetails.orderId
		}/lineItems/${refundDetails.lineItemId}/refunds?caseId=${refundDetails.caseId}&alerted=${
			refundDetails.alerted === null || !refundDetails.alerted ? false : refundDetails.alerted
		}&isConcession=${refundDetails.isConcession}`
		if (refundDetails.handleTime) {
			endpoint = `${endpoint}&handleTime=${refundDetails.handleTime}`
		}

		if (refundDetails.isOverpaymentAccepted) {
			endpoint = `${endpoint}&isOverpaymentAccepted=${refundDetails.isOverpaymentAccepted}`
		}

		const res = await axios
			.post(
				endpoint,
				{
					refundReason: refundDetails.reason,
					refundAmounts: refundDetails.refundAmounts,
				},
				getAxiosConfigWithAuth()
			)
			.then(() => {
				return true
			})
			.catch((err) => {
				console.error("error while attempting to refund an order: " + err)
				return false
			})
		return res
	}

	async refundMultipleItem(
		refundDetails: MultipleRefundDetails
	): Promise<RefundApiResponse | null> {
		let endpoint = `${process.env.REACT_APP_CARE_ORDER_SERVICE}/v2/orders/${refundDetails.orderId}/lineItems/refunds?caseId=${refundDetails.caseId}`

		try {
			const response: RefundApiResponse = await axios.post(
				endpoint,
				{
					lineItems: refundDetails.lineItems,
				},
				getAxiosConfigWithAuth()
			)
			return response
		} catch (error) {
			return null
		}
	}

	async creditItem(creditDetails: CreditItemDetails): Promise<boolean> {
		let endpoint = `${process.env.REACT_APP_CARE_ORDER_SERVICE}/v1/orders/${
			creditDetails.orderNumber
		}/lineItems/${creditDetails.lineItemId}/credits?caseId=${creditDetails.caseId}&alerted=${
			creditDetails.alerted === null || !creditDetails.alerted ? false : creditDetails.alerted
		}&isConcession=${creditDetails.isConcession ? creditDetails.isConcession : false}`
		if (creditDetails.handleTime) {
			endpoint = `${endpoint}&handleTime=${creditDetails.handleTime}`
		}

		if (creditDetails.isOverpaymentAccepted) {
			endpoint = `${endpoint}&isOverpaymentAccepted=${creditDetails.isOverpaymentAccepted}`
		}

		const res = await axios
			.post(
				endpoint,
				{
					creditReason: creditDetails.reason,
					itemUntaxtedPrice: creditDetails.creditAmounts.itemUntaxtedPrice,
					itemTaxAmount: creditDetails.creditAmounts.itemTaxAmount,
					shippingUntaxedPrice: creditDetails.creditAmounts.shippingUntaxedPrice,
					shippingTaxAmount: creditDetails.creditAmounts.shippingTaxAmount,
					currency: creditDetails.creditAmounts.currency,
					fractionDigits: creditDetails.creditAmounts.fractionDigits,
				},
				getAxiosConfigWithAuth()
			)
			.then(() => {
				return true
			})
			.catch((err) => {
				console.error("error while attempting to credit an item: " + err)
				return false
			})
		return res
	}

	async creditMultiItem(
		orderNumber: string,
		caseId: string,
		creditDetailsPayload: MultiLineItemCreditPayload
	): Promise<MultiCreditApiResponse | null> {
		try {
			let endpoint = `${
				process.env.REACT_APP_CARE_ORDER_SERVICE
			}/v1/orders/${orderNumber}/lineItems/credits?${caseId ? `&caseId=${caseId}` : ""}`

			const response: MultiCreditApiResponse = await axios.post(
				endpoint,
				creditDetailsPayload,
				getAxiosConfigWithAuth()
			)

			return response
		} catch (err) {
			return null
		}
	}

	async getFinanceHistory(orderNumber: string): Promise<PaymentHistory> {
		const endpoint = `${process.env.REACT_APP_CARE_ORDER_SERVICE}/v1/orders/${orderNumber}/ByOrderNumber/payments`
		return await axios
			.get(endpoint, getAxiosConfigWithAuth())
			.then((res) => {
				return res.data
			})
			.catch((err) => {
				console.error("error while attempting to get payment history " + err)
				return null
			})
	}

	async updateShippingAddress(
		shippingAddressRequest: ShippingAddressChangeRequest
	): Promise<boolean> {
		let endpoint = `${process.env.REACT_APP_CARE_ORDER_SERVICE}/v1/orders/${shippingAddressRequest.orderNumber}/shippingAddress?caseId=${shippingAddressRequest.caseId}`

		const res = await axios
			.patch(
				endpoint,
				{
					shippingAddress: shippingAddressRequest.shippingAddress,
					reason: shippingAddressRequest.reason,
				},
				getAxiosConfigWithAuth()
			)
			.then(() => {
				return true
			})
			.catch((err) => {
				console.error("error while attempting to request a shipping address change: " + err)
				return false
			})
		return res
	}

	async updateBillingAddress(billingAddressRequest: BillingAddressChangeRequest): Promise<boolean> {
		let endpoint = `${process.env.REACT_APP_CARE_ORDER_SERVICE}/v1/orders/${billingAddressRequest.orderNumber}/billingAddress?caseId=${billingAddressRequest.caseId}`

		const res = await axios
			.patch(
				endpoint,
				{
					billingAddress: billingAddressRequest.billingAddress,
					reason: billingAddressRequest.reason,
				},
				getAxiosConfigWithAuth()
			)
			.then(() => {
				return true
			})
			.catch((err) => {
				console.error("error while attempting to request a billing address change: " + err)
				return false
			})
		return res
	}

	async sendEmail(request: EmailRequest): Promise<SendEmailServiceResponse> {
		let endpoint = `${process.env.REACT_APP_CARE_ORDER_SERVICE}/v1/orders/${request.orderNumber}/events/${request.eventId}/resendEmail?caseId=${request.caseId}`
		const res = await axios
			.post(endpoint, {}, getAxiosConfigWithAuth())
			.then((response) => {
				return {
					toEmail: (response.data as SendEmailResponse).toEmail,
					success: true,
				}
			})
			.catch((err) => {
				console.error("error while attempting to resend a confirmation email: " + err)
				return {
					toEmail: "",
					success: false,
				}
			})
		return res
	}

	public async getReasons(): Promise<Array<string>> {
		const endpoint = `${process.env.REACT_APP_CARE_ORDER_SERVICE}/v1/reasons`
		const reasons = LocalStorageHandler.getData(endpoint) as Array<string>
		if (reasons) {
			return reasons
		} else {
			const data = await axios
				.get(endpoint, getAxiosConfigWithAuth())
				.then((res) => {
					return res.data as Array<string>
				})
				.catch((e) => {
					console.error(e)
					return []
				})
			LocalStorageHandler.saveData(endpoint, data)
			return data
		}
	}

	async getOrderCredits(orderNumber: string): Promise<OrderCredits | null> {
		const res = await axios
			.get(
				`${process.env.REACT_APP_CARE_ORDER_SERVICE}/v1/orders/${orderNumber}/credits`,
				getAxiosConfigWithAuth()
			)
			.then((res) => {
				return res.data as OrderCredits
			})
			.catch((e) => {
				console.error(e)
				return null
			})
		return res
	}

	async getIssuableAmountsForMC(orderNumber: string): Promise<IssuableAmountsForMC | null> {
		const res = await axios
			.get(
				`${process.env.REACT_APP_CARE_ORDER_SERVICE}/v1/orders/${orderNumber}/issuableAmountsForMerchandiseCredits`,
				getAxiosConfigWithAuth()
			)
			.then((res) => {
				return res.data as IssuableAmountsForMC
			})
			.catch((e) => {
				console.error(e)
				return null
			})
		return res
	}

	async creditOrderV3(creditDetails: CreditOrderDetails): Promise<boolean> {
		let endpoint = `${process.env.REACT_APP_CARE_ORDER_SERVICE}/v3/orders/${
			creditDetails.orderNumber
		}/credits?shippingOnly=${creditDetails.shippingOnly}&caseId=${creditDetails.caseId}&alerted=${
			creditDetails.alerted === null || !creditDetails.alerted ? false : creditDetails.alerted
		}&isConcession=${creditDetails.isConcession}`
		if (creditDetails.handleTime) {
			endpoint = `${endpoint}&handleTime=${creditDetails.handleTime}`
		}

		if (creditDetails.isOverpaymentAccepted) {
			endpoint = `${endpoint}&isOverpaymentAccepted=${creditDetails.isOverpaymentAccepted}`
		}

		const res = await axios
			.post(
				endpoint,
				{
					creditReason: creditDetails.reason,
				},
				getAxiosConfigWithAuth()
			)
			.then(() => {
				return true
			})
			.catch((err) => {
				console.error("error while attempting to credit an order: " + err)
				return false
			})
		return res
	}

	public async getOrderItemFailures(orderNumber: string): Promise<OrderItemFailure[]> {
		const res = await axios
			.get(
				`${process.env.REACT_APP_CARE_ORDER_SERVICE}/v1/orders/${orderNumber}/orderItemFailures`,
				getAxiosConfigWithAuth()
			)
			.then((res) => {
				return res.data as OrderItemFailure[]
			})
			.catch((e) => {
				console.error(e)
				return []
			})
		return res
	}
	public async getReplacementShippingMethods(
		orderNumber: string,
		body: ReplacementShippingMethodsRequests
	): Promise<ReplacementShippingMethodsResponse | null> {
		const res = await axios
			.post(
				`${process.env.REACT_APP_CARE_ORDER_SERVICE}/v1/orders/${orderNumber}/replacementShippingMethods`,
				body,
				getAxiosConfigWithAuth()
			)
			.then((res) => {
				return res.data as ReplacementShippingMethodsResponse
			})
			.catch((e) => {
				console.error(e)
				return null
			})
		return res
	}

	async replaceOrderV2(
		orderNumber: string,
		alerted: boolean,
		replacementBody: OrderReplacementRequest,
		handleTime?: number,
		caseId?: string,
		isConcession?: boolean,
		isMultiSelect?: boolean
	): Promise<ReplaceOrderResponseV2 | null> {
		let endpoint = `${
			process.env.REACT_APP_CARE_ORDER_SERVICE
		}/v2/orders/${orderNumber}/replacements?caseId=${caseId}&alerted=${
			alerted === null || !alerted ? false : alerted
		}&isConcession=${isConcession}`
		if (handleTime) {
			endpoint = `${endpoint}&handleTime=${handleTime}`
		}
		if (isMultiSelect) {
			endpoint = `${endpoint}&isMultiSelect=${isMultiSelect}`
		}
		const res = await axios
			.post(endpoint, replacementBody, getAxiosConfigWithAuth())
			.then((res) => {
				return res.data as ReplaceOrderResponseV2
			})
			.catch((err) => {
				console.log(`Error while attempting to replace the order: ${orderNumber}; ` + err)
				return null
			})

		return res
	}
	async replaceLineItemDocumentPreviousRevision(
		orderNumber: string,
		lineItemId: string,
		workId: string | null,
		workRevisionId: string | null,
		shopperId: string,
		handleTime?: number
	): Promise<any> {
		let endpoint = `${process.env.REACT_APP_CARE_ORDER_SERVICE}/v1/orders/${orderNumber}/lineItems/${lineItemId}/replaceLineItemDocumentPreviousRevision`

		if (handleTime) {
			endpoint = `${endpoint}?handleTime=${handleTime}`
		}

		const res = await axios
			.post(
				endpoint,
				{
					workId,
					workRevisionId,
					shopperId,
				},
				getAxiosConfigWithAuth()
			)
			.then((res) => {
				return res
			})
			.catch((err) => {
				console.log(
					`Error while attempting to replace line item document revision: ${lineItemId}; ` + err
				)
				return null
			})

		return res
	}
	public async getOrderEventsV2(
		orderNumber: string,
		limit: number,
		type: string,
		offset?: number
	): Promise<EventResponse> {
		const limitParam = limit > 0 ? `limit=${limit}&` : "limit=200&"
		const offsetParam = offset && offset > 0 ? `offset=${offset}&` : "offset=0&"
		const typeParam = type ? `type=${type}` : ""
		const res = await axios
			.get(
				`${process.env.REACT_APP_CARE_ORDER_SERVICE}/v2/orders/${orderNumber}/events?${limitParam}${typeParam}${offsetParam}`,
				getAxiosConfigWithAuth()
			)
			.then((res) => {
				return res.data as EventResponse
			})
			.catch((error) => {
				return {
					itemEvents: [],
					orderEvents: [],
				}
			})
		return res
	}
}

export interface ReplacementShippingMethodsRequests {
	itemReplacementShippingMethodRequests: LineItemReplacementRequest[]
}
interface LineItemReplacementRequest {
	lineItemId: string
	quantity: number
}
